import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Enhanced Countdown Hook
 * More reliable than RAF-based approach with better error handling
 */
const useEnhancedCountdown = (initialTime = 60000) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const intervalRef = useRef(null);
  const endTimeRef = useRef(null);
  const startTimeRef = useRef(null);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsActive(false);
  }, []);

  // Start countdown
  const start = useCallback((duration) => {
    cleanup(); // Clear any existing timer
    
    const startDuration = typeof duration === 'number' ? duration : initialTime;
    if (startDuration <= 0) {
      setTimeLeft(0);
      setIsActive(false);
      return;
    }

    const now = Date.now();
    startTimeRef.current = now;
    endTimeRef.current = now + startDuration;
    setIsActive(true);
    
    // Calculate initial time left
    const remaining = Math.max(0, endTimeRef.current - now);
    setTimeLeft(remaining);

    // Use regular setInterval for better reliability
    intervalRef.current = setInterval(() => {
      const currentTime = Date.now();
      const remaining = Math.max(0, endTimeRef.current - currentTime);
      
      setTimeLeft(remaining);
      
      if (remaining <= 0) {
        cleanup();
      }
    }, 1000); // Update every second

  }, [initialTime, cleanup]);

  // Pause countdown
  const pause = useCallback(() => {
    cleanup();
  }, [cleanup]);

  // Resume countdown
  const resume = useCallback(() => {
    if (timeLeft > 0) {
      start(timeLeft);
    }
  }, [timeLeft, start]);

  // Reset countdown
  const reset = useCallback(() => {
    cleanup();
    setTimeLeft(0);
    endTimeRef.current = null;
    startTimeRef.current = null;
  }, [cleanup]);

  // Get remaining time from localStorage key
  const getStoredTimeLeft = useCallback((storageKey) => {
    try {
      const storedEndTime = localStorage.getItem(storageKey);
      if (storedEndTime) {
        const endTime = parseInt(storedEndTime, 10);
        const now = Date.now();
        return Math.max(0, endTime - now);
      }
    } catch (error) {
      console.warn('Error reading from localStorage:', error);
    }
    return 0;
  }, []);

  // Start with localStorage persistence
  const startWithStorage = useCallback((storageKey, duration) => {
    const startDuration = typeof duration === 'number' ? duration : initialTime;
    
    // Check if we have a stored end time
    const storedTimeLeft = getStoredTimeLeft(storageKey);
    
    if (storedTimeLeft > 0) {
      // Resume from stored time
      start(storedTimeLeft);
    } else {
      // Start fresh and store end time
      try {
        const endTime = Date.now() + startDuration;
        localStorage.setItem(storageKey, endTime.toString());
        start(startDuration);
      } catch (error) {
        console.warn('Error writing to localStorage:', error);
        start(startDuration);
      }
    }
  }, [initialTime, start, getStoredTimeLeft]);

  // Clear storage
  const clearStorage = useCallback((storageKey) => {
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('Error clearing localStorage:', error);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    timeLeft,
    isActive,
    start,
    pause,
    resume,
    reset,
    startWithStorage,
    clearStorage,
    getStoredTimeLeft
  };
};

export default useEnhancedCountdown; 